<script setup lang="ts">
import { computed } from "vue";

const {
  fields = [],
  hasOther = false,
  heading = "",
  otherLabel = "",
} = defineProps<{
  fields?: string[]
  hasOther?: boolean
  heading?: string
  otherLabel?: string
}>();

const headingText = computed(() => heading.replace(/[:：]\s*$/, ""));
</script>

<template>
  <div class="dirty-fields">
    <p v-if="headingText" class="heading">
      {{ headingText }}
    </p>
    <ul>
      <li
        v-for="(field, index) in fields"
        :key="`${field}-${index}`"
        class="item"
        :style="{ animationDelay: `${Math.min(index, 8) * 40}ms` }"
      >
        <span class="marker" aria-hidden="true" />
        <span class="label">{{ field }}</span>
      </li>
    </ul>
    <p v-if="hasOther && otherLabel" class="other">
      {{ otherLabel }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.dirty-fields {
  width: 100%;
  max-height: 9.5rem;
  overflow: auto;
  padding: 0.625rem 0.75rem;
  border-radius: 12px;
  background: var(--background-dark);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--text) 8%, transparent),
    0 1px 2px -1px color-mix(in srgb, var(--text) 6%, transparent);
  text-align: left;

  scrollbar-width: thin;
  scrollbar-color: var(--input-border) transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--input-border);
    border-radius: 4px;
  }
}

.heading {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  text-wrap: balance;
  color: var(--text-2);
}

ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  animation: dirty-field-in 0.2s cubic-bezier(0.2, 0, 0, 1) both;
}

.item + .item {
  margin-top: 0.3rem;
}

.marker {
  flex: 0 0 auto;
  width: 0.375rem;
  height: 0.375rem;
  margin-top: 0.375rem;
  border-radius: 9999px;
  background: var(--gray-1);
}

.label {
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.35;
  text-wrap: pretty;
  color: var(--text);
}

.other {
  margin: 0.55rem 0 0;
  padding-left: 0.875rem;
  font-size: 0.75rem;
  line-height: 1.35;
  text-wrap: pretty;
  color: var(--text-2);
}

@keyframes dirty-field-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
